import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Collapse, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import { CONTENT_SHAPE } from '../../constants/contentColors';
import { materialIcon, normalizeMaterialKey } from '../../constants/zeroIcons';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { accentRamp, getChartTokens } from '../../theme/chartTheme';
import { contentBorders } from '../../theme/createContentTheme';
import type { WasteTypeWithMaterials } from '../../types/dashboard.type';

/** Diameter (px) of the category donut. */
const DONUT_SIZE = 220;

/**
 * Material intelligence: each waste category with its icon, collected mass,
 * share of the total and a bar in one hue at decreasing strength. A row
 * expands to the organisation's own material names (the dashboard API folds
 * newer catalog categories into "other", so the names are the honest detail).
 * `showChart` adds a donut of the same categories; a slice opens its row.
 */
const MaterialList: React.FC<{ breakdown: WasteTypeWithMaterials[]; ariaLabel: string; showChart?: boolean }> = ({
  breakdown,
  ariaLabel,
  showChart = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const borders = contentBorders(theme);
  const [open, setOpen] = useState<string | null>(null);

  const rows = useMemo(
    () =>
      breakdown
        .filter((row) => Number.isFinite(row.total_kg) && row.total_kg > 0)
        .sort((a, b) => b.total_kg - a.total_kg),
    [breakdown],
  );
  const total = rows.reduce((sum, row) => sum + row.total_kg, 0);
  const max = rows[0]?.total_kg ?? 0;
  const ramp = accentRamp(theme, Math.max(rows.length, 1));
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const labelOf = (wasteType: string) => t(`materials.${normalizeMaterialKey(wasteType)}`, { defaultValue: wasteType });

  const list = (
    <Box component="ul" aria-label={ariaLabel} sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0 }}>
      {rows.map((row, index) => {
        const key = normalizeMaterialKey(row.waste_type);
        const Icon = materialIcon(row.waste_type);
        const label = t(`materials.${key}`, { defaultValue: row.waste_type });
        const materials = (row.materials ?? []).filter((m) => m.amount > 0).sort((a, b) => b.amount - a.amount);
        const expandable = materials.length > 0;
        const expanded = open === row.waste_type;
        const width = max > 0 ? Math.max((row.total_kg / max) * 100, 1.5) : 0;
        const kg = fmt.metric('mass_kg', row.total_kg, { scaleMass: true });
        return (
          <Box component="li" key={row.waste_type} sx={{ borderBottom: `1px solid ${borders.subtle}`, '&:last-of-type': { borderBottom: 0 } }}>
            <ButtonBase
              disabled={!expandable}
              onClick={() => setOpen(expanded ? null : row.waste_type)}
              aria-expanded={expandable ? expanded : undefined}
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '32px minmax(0, 1fr) auto auto 20px',
                alignItems: 'center',
                columnGap: 1.5,
                py: 1.25,
                px: 0.5,
                textAlign: 'start',
                borderRadius: `${CONTENT_SHAPE.xs}px`,
                '&:hover': expandable ? { bgcolor: 'action.hover' } : undefined,
                '&.Mui-disabled': { color: 'inherit' },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 32,
                  height: 32,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: `${CONTENT_SHAPE.sm}px`,
                  bgcolor: alpha(ramp[index], 0.14),
                  color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
                <Box aria-hidden sx={{ mt: 0.75, height: 4, borderRadius: '2px', bgcolor: borders.subtle, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${width}%`,
                      height: '100%',
                      bgcolor: ramp[index],
                      transition: 'width 400ms ease',
                      animation: 'zero-grow-width 800ms ease-out',
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                <bdi>{kg.text}</bdi>
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, marginInlineStart: 0.5 }}>
                  {kg.unit}
                </Box>
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 44, textAlign: 'end', fontVariantNumeric: 'tabular-nums' }}>
                <bdi>{fmt.share(total > 0 ? row.total_kg / total : null)}</bdi>
              </Typography>
              <ExpandMoreRounded
                aria-hidden
                sx={{
                  fontSize: 18,
                  color: 'text.secondary',
                  visibility: expandable ? 'visible' : 'hidden',
                  transform: expanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms ease',
                }}
              />
            </ButtonBase>
            {expandable ? (
              <Collapse in={expanded} unmountOnExit>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, pb: 1.25, paddingInlineStart: 7, paddingInlineEnd: 4 }}>
                  {materials.map((m) => (
                    <Box
                      component="li"
                      key={m.material_type}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
                    >
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {m.material_type}
                      </Typography>
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        <bdi>{fmt.withUnit('mass_kg', m.amount)}</bdi>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );

  if (!showChart || rows.length === 0) return list;

  const totalKg = fmt.metric('mass_kg', total, { scaleMass: true });
  const summary = rows.map((row) => `${labelOf(row.waste_type)}: ${fmt.share(total > 0 ? row.total_kg / total : null)}`).join(', ');
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `${DONUT_SIZE}px minmax(0, 1fr)` },
        alignItems: 'center',
        gap: { xs: 2, sm: 4 },
      }}
    >
      <Box
        role="img"
        aria-label={`${ariaLabel}. ${summary}`}
        sx={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE, mx: { xs: 'auto', sm: 0 } }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="total_kg"
              nameKey="waste_type"
              innerRadius="64%"
              outerRadius="96%"
              paddingAngle={rows.length > 1 ? 1.5 : 0}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
              startAngle={90}
              endAngle={-270}
              {...tokens.animation}
            >
              {rows.map((row, index) => (
                <Cell
                  key={row.waste_type}
                  fill={ramp[index]}
                  cursor={(row.materials ?? []).some((m) => m.amount > 0) ? 'pointer' : 'default'}
                  onClick={() => {
                    if ((row.materials ?? []).some((m) => m.amount > 0)) setOpen(row.waste_type);
                  }}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={tokens.tooltip.contentStyle}
              itemStyle={tokens.tooltip.itemStyle}
              formatter={(value: number, name: string) => [fmt.withUnit('mass_kg', value, { scaleMass: true }), labelOf(name)]}
              separator=": "
            />
          </PieChart>
        </ResponsiveContainer>
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center', pointerEvents: 'none' }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.75rem', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
              <bdi>{totalKg.text}</bdi>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalKg.unit}
            </Typography>
          </Box>
        </Box>
      </Box>
      {list}
    </Box>
  );
};

export default MaterialList;
